import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Check2, InfoOutline } from '@crehana/icons';

import { greenMain, redMain } from '@crehana/ui/dist/styles/colors';

import { CrehanaTFunction, useTranslation } from '@crehana/i18n';

import { CheckoutTKeys } from '../../../i18n/language';

import { DEFAULT_INPUT_MAX_LENGTH, LABEL_WARNING_MESSAGE } from './constants';
import { InputMode, InputState, Validation } from './types';
import { Mask } from './utils';
import { StyledInput } from './styles/StyledInput';

const generateId = (label: string) =>
  label
    .toLowerCase()
    .normalize('NFD')
    .replace(/\s/g, '-')
    .replace(/[\u0300-\u036f]/g, '');

const defaultValidation =
  (
    required: boolean,
    value: string,
    t: CrehanaTFunction<CheckoutTKeys>,
  ): Validation =>
  () => {
    if (required && value === '')
      return { isValid: false, errorMessage: t('REQUIRED_FIELD') };
    return { isValid: true, errorMessage: '' };
  };

type Props = {
  onChange: (state: InputState) => void;
  validation?: Validation;
  label: string;
  initialValue?: string;
  information?: string;
  hideLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  disabledMessage?: string;
  darkMode?: boolean;
  mask?: Mask;
  maxLength?: number;
  inputMode?: InputMode;
  required?: boolean;
};

const InputField: React.FC<Props> = ({
  onChange,
  validation,
  label,
  placeholder = '',
  initialValue,
  information,
  disabledMessage,
  hideLabel = false,
  disabled = false,
  darkMode = false,
  required = false,
  inputMode = 'text',
  mask,
  maxLength,
}) => {
  if (!label) console.warn(LABEL_WARNING_MESSAGE);

  const [firstInput, setFirstInput] = useState(true);
  const [value, setValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [cursorPosition, setCursorPosition] = useState<number | null>();

  const input = useRef<HTMLInputElement | null>(null);
  const inputId = generateId(label);
  const hasError = errorMessage !== '' && !firstInput;
  const { t } = useTranslation<CheckoutTKeys>();
  const validate = validation || defaultValidation(required, value, t);

  useEffect(() => {
    if (!mask || !initialValue) return;

    const { maskedValue } = mask.format(initialValue, {
      previousValue: '',
      selectionStart: 0,
    });

    setValue(maskedValue);
  }, [initialValue, mask]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = event.currentTarget;
    const { selectionStart } = event.target;

    const { maskedValue, cursorPosition: newCursorPosition } = mask?.format(
      inputValue,
      {
        previousValue: value,
        selectionStart: selectionStart || 0,
      },
    ) || { maskedValue: inputValue, cursorPosition: selectionStart };

    const rawValue = mask?.unformat(maskedValue) || inputValue;
    const validationResult = validate({ rawValue, maskedValue });
    const isValid = validationResult.isValid && !(required && rawValue === '');

    setCursorPosition(newCursorPosition);
    setErrorMessage(validationResult.errorMessage);
    onChange({
      value: rawValue,
      maskedValue,
      isValid,
    });
    setValue(maskedValue);
  };

  useEffect(() => {
    if (input.current && cursorPosition) {
      input.current.selectionStart = cursorPosition;
      input.current.selectionEnd = cursorPosition;
    }
  }, [cursorPosition]);

  const handleBlur = () => {
    setFirstInput(false);

    const cleanValue = value.trim().replace(/  +/g, ' ');
    const validationResult = validate({
      rawValue: mask?.unformat(cleanValue) || cleanValue,
      maskedValue: cleanValue,
    });

    setErrorMessage(validationResult.errorMessage);
    setValue(cleanValue);
  };

  const labelColor = darkMode ? 'text-white' : 'text-base-main';
  const informationColor = darkMode ? 'text-base-lighter' : 'text-gray-dark';
  const inputBackground = darkMode ? 'bg-base-lighter-dark-mode' : 'bg-white';

  return (
    <div className="w-full">
      <label
        className={`block mb-8 font-subtitle3 ${labelColor}`}
        htmlFor={inputId}
        hidden={hideLabel}
      >
        {label}
        {required && <span className="text-red-main">*</span>}
      </label>
      {information && (
        <p className={`font-body4 mb-8 ${informationColor}`}>{information}</p>
      )}
      <div className="relative flex items-center">
        <StyledInput
          className={`w-full h-56 border border-solid outline-none rounded-5 py-16 pl-16 pr-40 font-body3 ${labelColor} ${inputBackground}`}
          ref={input}
          id={inputId}
          name={inputId}
          type="text"
          placeholder={placeholder}
          hasError={hasError}
          disabled={disabled}
          darkMode={darkMode}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-errormessage="error-message"
          aria-invalid={hasError || disabled}
          value={value}
          maxLength={maxLength || mask?.maxLength || DEFAULT_INPUT_MAX_LENGTH}
          inputMode={inputMode}
        />
        {hasError && (
          <span className="absolute mr-16 right-0">
            <InfoOutline size={24} color={redMain} />
          </span>
        )}
        {!hasError && !firstInput && (
          <span className="absolute mr-16 right-0">
            <Check2 size={24} color={greenMain} />
          </span>
        )}
      </div>
      {disabled && disabledMessage && (
        <p id="error-message" className="mt-8 text-gray-dark font-caption">
          {disabledMessage}
        </p>
      )}
      {hasError && !disabled && (
        <p id="error-message" className="mt-8 text-red-main font-caption">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default InputField;
